/**
 * CRM Pipeline Column
 *
 * Droppable column component for the Kanban board
 */

import { useDroppable } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CrmOpportunityCard } from './CrmOpportunityCard';
import { DEFAULT_CURRENCY } from '../../constants';
import type { PipelineColumn, PipelineOpportunity } from '../../types';

interface CrmColumnProps {
  column: PipelineColumn;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onViewOpportunity?: (opportunity: PipelineOpportunity) => void;
  onAddActivity?: (opportunity: PipelineOpportunity) => void;
  onCreateQuote?: (opportunity: PipelineOpportunity) => void;
  onMarkLost?: (opportunity: PipelineOpportunity) => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

export function CrmColumn({
  column,
  collapsed = false,
  onToggleCollapsed,
  onViewOpportunity,
  onAddActivity,
  onCreateQuote,
  onMarkLost,
  onLoadMore,
  isLoadingMore = false,
}: CrmColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.key,
    data: { column },
  });

  const remainingCount = column.count - column.loadedCount;

  if (collapsed) {
    return (
      <div className="flex flex-col w-10 min-w-10 flex-shrink-0">
        <div
          className="rounded-t-lg px-1 py-2 border border-b-0 flex items-center justify-center"
          style={{ backgroundColor: column.bgColor, borderColor: column.color }}
        >
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="p-1 rounded hover:bg-black/5 text-gray-600"
            title="Expand lane"
            aria-label={`Expand ${column.title}`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div
          ref={setNodeRef}
          className={cn(
            'flex-1 min-h-[200px] max-h-[calc(100vh-280px)] overflow-hidden',
            'bg-gray-50 border border-t-0 border-gray-200 rounded-b-lg',
            'flex flex-col items-center py-3 gap-3 transition-colors',
            isOver && 'bg-primary/5 ring-2 ring-primary/20',
          )}
        >
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: column.color, color: 'white' }}
          >
            {column.count}
          </span>
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="text-xs font-medium text-gray-700 hover:text-gray-900 text-left"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            title={`Expand ${column.title}`}
          >
            {column.title}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-72 min-w-72 flex-shrink-0">
      {/* Column Header */}
      <div
        className="rounded-t-lg px-3 py-2 border border-b-0"
        style={{ backgroundColor: column.bgColor, borderColor: column.color }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: column.color }}
            />
            <h3 className="font-medium text-sm text-gray-900 truncate">{column.title}</h3>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: column.color, color: 'white' }}
            >
              {column.count}
            </span>
            {onToggleCollapsed && (
              <button
                type="button"
                onClick={onToggleCollapsed}
                className="p-0.5 rounded text-gray-500 hover:bg-black/5 hover:text-gray-700"
                title="Collapse lane"
                aria-label={`Collapse ${column.title}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Column Value */}
        {column.totalValue > 0 && (
          <div className="mt-1 text-xs text-gray-600">
            {formatCurrency(column.totalValue, DEFAULT_CURRENCY)} total
          </div>
        )}
      </div>

      {/* Column Body - Droppable area */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex-1 min-h-[200px] max-h-[calc(100vh-280px)] overflow-y-auto',
          'bg-gray-50 border border-t-0 border-gray-200 rounded-b-lg p-2',
          'transition-colors duration-200',
          isOver && 'bg-primary/5 ring-2 ring-primary/20'
        )}
      >
        <div className="space-y-2">
          {column.opportunities.map((opportunity) => (
            <CrmOpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              onView={onViewOpportunity}
              onAddActivity={onAddActivity}
              onCreateQuote={onCreateQuote}
              onMarkLost={onMarkLost}
            />
          ))}

          {/* Load More Button */}
          {column.hasMore && onLoadMore && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              onClick={onLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Loading...
                </>
              ) : (
                <>Load more ({remainingCount} remaining)</>
              )}
            </Button>
          )}

          {/* Empty state */}
          {column.opportunities.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="text-gray-400 text-sm">No opportunities</div>
              <div className="text-gray-300 text-xs mt-1">Drag opportunities here</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
